import React, { useState, useRef, useEffect } from "react";
import api, { apiError } from "@/lib/api";
import { viewAttachment } from "@/lib/files";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InlineSpinner } from "@/components/common/Loading";
import { Sparkles, Send, FileText, ExternalLink, BookOpen } from "lucide-react";
import { toast } from "sonner";

// Minimal, safe markdown -> HTML for assistant answers (text is escaped first).
const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
export function mdToHtml(md) {
  const lines = esc(md || "").split("\n");
  let out = "", list = null;
  const inline = (s) => s.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>").replace(/(^|\W)\*(?!\s)(.+?)\*(?=\W|$)/g, "$1<em>$2</em>");
  let table = null;
  const flushTable = () => { if (table) { out += `<table>${table.map((r, i) => `<tr>${r.map((c) => `<${i ? "td" : "th"}>${inline(c)}</${i ? "td" : "th"}>`).join("")}</tr>`).join("")}</table>`; table = null; } };
  const close = () => { flushTable(); if (list) { out += `</${list}>`; list = null; } };
  for (const raw of lines) {
    const l = raw.trim();
    if (/^\|.*\|$/.test(l)) {
      if (list) { out += `</${list}>`; list = null; }
      if (/^\|[\s:|-]+\|$/.test(l)) continue;
      (table = table || []).push(l.slice(1, -1).split("|").map((c) => c.trim()));
      continue;
    }
    flushTable();
    const h = l.match(/^#{1,4}\s+(.*)/), ul = l.match(/^[-*•]\s+(.*)/), ol = l.match(/^\d+[.)]\s+(.*)/);
    if (h) { close(); out += `<h3>${inline(h[1])}</h3>`; }
    else if (ul || ol) { const t = ul ? "ul" : "ol"; if (list !== t) { close(); out += `<${t}>`; list = t; } out += `<li>${inline((ul || ol)[1])}</li>`; }
    else if (!l || /^-{3,}$/.test(l)) close();
    else { close(); out += `<p>${inline(l)}</p>`; }
  }
  close();
  return out;
}


// Reusable "Ask the Knowledge Center" panel. Grounded strictly on the caller's allowed published
// sources (Claude Sonnet 4.6); shows the answer + cited sources with a secure inline view link.
export function AskKnowledge({ compact = false }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [turns, setTurns] = useState([]); // {q, answer, grounded, citations}
  const sessionRef = useRef(`ka-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const [status, setStatus] = useState(null);
  const endRef = useRef(null);

  useEffect(() => { api.get("/knowledge-assistant/status").then((r) => setStatus(r.data)).catch(() => setStatus(null)); }, []);
  useEffect(() => {
    if (turns.length === 0 && !busy) return; // don't hijack page scroll on initial mount
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [turns, busy]);

  const ask = async () => {
    const question = q.trim();
    if (!question || busy) return;
    setBusy(true); setQ("");
    try {
      const { data } = await api.post("/knowledge-assistant/ask", { question, session_id: sessionRef.current });
      setTurns((t) => [...t, { q: question, ...data }]);
    } catch (e) { toast.error(apiError(e)); setQ(question); }
    finally { setBusy(false); }
  };

  const openCite = async (c) => {
    if (c.source_type !== "document") return;
    try { await viewAttachment(`/knowledge-assistant/sources/${c.source_id}/file`, c.title); }
    catch { toast.error("Could not open source"); }
  };

  return (
    <div className="rounded-2xl border border-[#006090]/20 bg-white overflow-hidden" data-testid="ask-knowledge">
      <div className="bg-gradient-to-r from-[#006090] to-[#0093D6] px-5 py-4 text-white">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5" />
          <div>
            <p className="font-semibold text-sm">Ask the Knowledge Center</p>
            <p className="text-[11px] text-white/80">Answers use approved Knowledge Center sources, with citations.</p>
          </div>
        </div>
      </div>

      <div className={`px-5 py-4 space-y-4 ${compact ? "max-h-[360px]" : "max-h-[520px]"} overflow-y-auto tr-scroll`}>
        {turns.length === 0 && !busy && (
          <div className="text-center py-6 text-slate-400" data-testid="ask-knowledge-empty">
            <BookOpen className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">Ask about programs, licensing, becoming an agent or growing an agency.</p>
          </div>
        )}
        {turns.map((t, i) => (
          <div key={i} className="space-y-2" data-testid={`ask-turn-${i}`}>
            <div className="flex justify-end">
              <p className="rounded-2xl rounded-br-sm bg-[#006090] text-white text-sm px-3.5 py-2 max-w-[85%]">{t.q}</p>
            </div>
            <div className="rounded-2xl rounded-bl-sm bg-slate-50 border border-slate-200 px-3.5 py-2.5 max-w-[92%]">
              {t.mode === "unverified" && <p className="mb-1.5 text-[11px] font-semibold text-amber-700" data-testid={`ask-unverified-${i}`}>Could not be verified in an official source — please confirm with your district office.</p>}
              <div className="kc-article text-sm text-slate-800" data-testid={`ask-answer-${i}`} dangerouslySetInnerHTML={{ __html: mdToHtml(t.answer) }} style={{ overflowX: "auto" }} />
              {t.grounded && t.citations?.length > 0 && (
                <div className="mt-2.5 pt-2.5 border-t border-slate-200">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">Sources</p>
                  <div className="space-y-1">
                    {t.citations.map((c, j) => (
                      <button key={j} onClick={() => openCite(c)} disabled={c.source_type !== "document"}
                        data-testid={`ask-cite-${i}-${j}`}
                        className={`flex items-center gap-2 text-left w-full text-xs rounded-md px-2 py-1.5 ${c.source_type === "document" ? "text-[#006090] hover:bg-[#006090]/5" : "text-slate-600 cursor-default"}`}>
                        <FileText className="w-3.5 h-3.5 shrink-0" />{c.ref && <span className="shrink-0 font-semibold">[{c.ref}]</span>}
                        <span className="min-w-0 truncate">{c.title}{c.author ? ` (${c.author}${c.edition ? `, ${c.edition}` : ""})` : ""}{c.page ? ` · p.${c.page}` : ""}{c.effective_date ? ` · ${c.effective_date}` : ""}</span>
                        {c.tier_label && <span className="shrink-0 rounded bg-slate-100 px-1 text-[10px] text-slate-500" data-testid={`ask-cite-tier-${i}-${j}`}>{c.tier_label}</span>}
                        {c.source_type === "document" && <ExternalLink className="w-3 h-3 shrink-0 ml-auto" />}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
        {busy && <div className="flex items-center gap-2 text-sm text-slate-400" data-testid="ask-knowledge-loading"><InlineSpinner /> Searching your knowledge sources…</div>}
        <div ref={endRef} />
      </div>

      <div className="border-t border-slate-100 p-3 flex items-center gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()}
          placeholder={status && status.source_count === 0 ? "No published sources yet…" : "Ask a question…"}
          data-testid="ask-knowledge-input" disabled={busy} />
        <Button onClick={ask} disabled={busy || !q.trim()} className="bg-[#006090] hover:bg-[#004E75]" data-testid="ask-knowledge-send">
          <Send className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

export default AskKnowledge;
